import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { config } from '../config';
import * as fmt from '../format';
import type { MeasuredPath } from '../maps';
import {
  ROUTE_STROKE,
  describePoint,
  loadMaps,
  measurePath,
  nearestPointOnPath,
  positionAtMiles,
} from '../maps';
import type { Route } from '../models';
import type { Store } from '../store';
import type { CheckpointSeed } from './Checkpoint';
import { CheckpointDialog } from './Checkpoint';

/// Where you have got to, on the route the walk follows.
///
/// The position is derived, not stored: it is the point `totalActualMiles`
/// along the line. Nothing extra has to be recorded, nothing can drift out of
/// step with the mileage, and it is correct for historical figures as much as
/// today's. The store already refreshes every minute, so this follows without
/// a timer of its own.
///
/// The map is also where a checkpoint is picked. A checkpoint is a mileage —
/// the API stores nothing but a number and a label — and picking it off the
/// line is the same mileage arrived at by pointing rather than arithmetic.

/// Street View panorama loads are billed individually, and the position moves
/// every time a step is recorded. Below this the view would not visibly change
/// anyway, so it is left alone.
const STREET_VIEW_MOVE_METRES = 25;

/// How far off the route to look for a panorama. Interstates are covered well;
/// a line that cuts a corner across country may be a little way from the
/// nearest photographed road.
const STREET_VIEW_RADIUS_METRES = 250;

/// How wide a band around the route counts as clicking it. The drawn line is
/// 4px, which is a hard thing to hit with a mouse and an unreasonable thing to
/// ask of a fingertip, so the clicks have a wider invisible line of their own.
const ROUTE_HIT_WIDTH = 20;

export function MapView({ store }: { store: Store }) {
  const mapHolder = useRef<HTMLDivElement | null>(null);
  const panoHolder = useRef<HTMLDivElement | null>(null);

  const maps = useRef<typeof google.maps | null>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const panorama = useRef<google.maps.StreetViewPanorama | null>(null);
  const streetView = useRef<google.maps.StreetViewService | null>(null);
  const line = useRef<google.maps.Polyline | null>(null);
  const hit = useRef<google.maps.Polyline | null>(null);
  const path = useRef<MeasuredPath | null>(null);
  /// Where Street View was last pointed, so it is only moved when the view
  /// would actually differ.
  const lastPano = useRef<google.maps.LatLng | null>(null);
  const started = useRef(false);

  /// The checkpoint already set, and the one being placed.
  const checkpoint = useRef<google.maps.Marker | null>(null);
  const pin = useRef<google.maps.Marker | null>(null);

  const [route, setRoute] = useState<Route | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<'unknown' | 'yes' | 'no'>('unknown');
  const [position, setPosition] = useState<{ miles: number; heading: number } | null>(null);
  const [drawnMiles, setDrawnMiles] = useState<number | null>(null);
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState<{ miles: number } | null>(null);
  const [naming, setNaming] = useState(false);
  const [seed, setSeed] = useState<CheckpointSeed | null>(null);

  const routeId = store.walk?.routeId ?? null;
  const walked = store.summary?.totalActualMiles ?? 0;
  const tracking = store.walk?.targetTracking ?? null;

  /// The map's listeners outlive the render that created them, so anything
  /// they read has to be a ref rather than a captured value. Mileage in
  /// particular changes under them every time the store refreshes.
  const walkedNow = useRef(walked);
  walkedNow.current = walked;
  const arming = useRef(false);
  arming.current = placing;

  // Fetch the route whenever the walk points at a different one.
  useEffect(() => {
    if (!routeId) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    void api.route(routeId)
      .then((found) => { if (!cancelled) setRoute(found); })
      .catch((failure: unknown) => {
        if (!cancelled) setError(failure instanceof Error ? failure.message : String(failure));
      });
    return () => { cancelled = true; };
  }, [routeId]);

  /// Take the marker being placed off the map and leave placement mode.
  const clearDraft = useCallback(() => {
    pin.current?.setMap(null);
    pin.current = null;
    setDraft(null);
    setPlacing(false);
    setNaming(false);
    map.current?.setOptions({ draggableCursor: null });
  }, []);

  /**
   * Put the marker being placed at the point on the line nearest `at`.
   *
   * Held back at the walker's own position rather than clamped silently at
   * either end: a checkpoint behind you is one the API would immediately
   * report as reached, which is not a target at all. Stopping the marker dead
   * where you are standing says that in the gesture; the note beneath says it
   * in words.
   *
   * Reads only refs, so the marker's own listeners can call it for the life of
   * the map without going stale.
   */
  const placeDraft = useCallback((at: google.maps.LatLng) => {
    const loaded = maps.current;
    const measured = path.current;
    if (!loaded || !map.current || !measured) return;

    const found = nearestPointOnPath(loaded, measured, at);
    if (!found) return;

    const here = walkedNow.current;
    const behind = found.miles < here;
    const held = behind ? positionAtMiles(loaded, measured.points, here) : null;
    const point = held?.point ?? found.point;
    const miles = behind ? here : found.miles;

    if (pin.current) pin.current.setPosition(point);
    else {
      const dropped = new loaded.Marker({
        position: point,
        map: map.current,
        draggable: true,
        title: 'Drag along the route',
        // Distinguishable from "you are here" without a bitmap to host:
        // a pin in the route's own colour.
        icon: {
          path: loaded.SymbolPath.BACKWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: ROUTE_STROKE,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        zIndex: 3,
      });
      // Snapping mid-gesture fights the drag, so the position is only
      // corrected on release. The readout still follows the pointer, which is
      // what makes the line feel like the thing being dragged along.
      dropped.addListener('drag', () => {
        const loose = dropped.getPosition();
        const measuredNow = path.current;
        if (!loose || !measuredNow || !maps.current) return;
        const over = nearestPointOnPath(maps.current, measuredNow, loose);
        if (!over) return;
        setDraft({ miles: Math.max(over.miles, walkedNow.current) });
      });
      dropped.addListener('dragend', () => {
        const dropTo = dropped.getPosition();
        if (dropTo) placeDraft(dropTo);
      });
      pin.current = dropped;
    }

    setDraft({ miles });
  }, []);

  // Build the map and the panorama once.
  useEffect(() => {
    if (started.current || !config.mapsKey) return;
    started.current = true;

    void (async () => {
      try {
        const loaded = await loadMaps();
        maps.current = loaded;
        if (!mapHolder.current || !panoHolder.current) return;

        map.current = new loaded.Map(mapHolder.current, {
          center: { lat: 39.5, lng: -98.35 },
          zoom: 4,
          mapTypeControl: false,
          // Its own panorama is below; the map's pegman would open a second.
          streetViewControl: false,
          fullscreenControl: false,
        });
        streetView.current = new loaded.StreetViewService();
        panorama.current = new loaded.StreetViewPanorama(panoHolder.current, {
          visible: true,
          addressControl: false,
          fullscreenControl: false,
          // Moving off the route would desync the view from the position it
          // claims to show, so the arrows are off.
          linksControl: false,
          clickToGo: false,
        });
        setReady(true);
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure));
      }
    })();
  }, []);

  // Draw the route when both it and the map are available.
  useEffect(() => {
    const loaded = maps.current;
    if (!ready || !loaded || !map.current || !route) return;

    // A route can change under this - picking another one on Manage - and the
    // old lines would otherwise stay drawn with nothing holding a handle to
    // them.
    clearDraft();

    const measured = measurePath(loaded, loaded.geometry.encoding.decodePath(route.polyline));
    path.current = measured;
    setDrawnMiles(measured.miles);

    line.current?.setMap(null);
    hit.current?.setMap(null);

    line.current = new loaded.Polyline({
      path: measured.points,
      map: map.current,
      strokeColor: ROUTE_STROKE,
      strokeWeight: 4,
      clickable: false,
    });

    // The click target: the same line, wide enough to hit and invisible. A
    // click is not a drag, so this does not stop the map being panned across
    // the route.
    const target = new loaded.Polyline({
      path: measured.points,
      map: map.current,
      strokeOpacity: 0,
      strokeWeight: ROUTE_HIT_WIDTH,
      clickable: true,
    });
    target.addListener('click', (event: google.maps.PolyMouseEvent) => {
      const at = event.latLng;
      // Only while placing: otherwise every click meant for the map would move
      // a checkpoint nobody was editing.
      if (at && arming.current) placeDraft(at);
    });
    hit.current = target;

    const bounds = new loaded.LatLngBounds();
    for (const point of measured.points) bounds.extend(point);
    map.current.fitBounds(bounds);
  }, [ready, route, clearDraft, placeDraft]);

  // Show the checkpoint that is set, at its own mileage along the line. Same
  // derivation as the walker's position, from the one number the API stores.
  useEffect(() => {
    const loaded = maps.current;
    const measured = path.current;
    if (!ready || !loaded || !map.current || !measured) return;

    if (!tracking) {
      checkpoint.current?.setMap(null);
      checkpoint.current = null;
      return;
    }

    const at = positionAtMiles(loaded, measured.points, tracking.miles);
    if (!at) return;
    const title = tracking.label
      ? `${tracking.label} — ${fmt.miles(tracking.miles)}`
      : `Checkpoint — ${fmt.miles(tracking.miles)}`;

    if (checkpoint.current) {
      checkpoint.current.setPosition(at.point);
      checkpoint.current.setTitle(title);
      return;
    }
    checkpoint.current = new loaded.Marker({
      position: at.point,
      map: map.current,
      title,
      icon: {
        path: loaded.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: ROUTE_STROKE,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
      zIndex: 2,
    });
  }, [ready, route, tracking?.miles, tracking?.label]);

  /// Put the marker, and the camera, at the current mileage.
  const place = useCallback(() => {
    const loaded = maps.current;
    const measured = path.current;
    if (!loaded || !map.current || !measured || measured.points.length === 0) return;

    const at = positionAtMiles(loaded, measured.points, walked);
    if (!at) return;

    if (marker.current) marker.current.setPosition(at.point);
    else {
      marker.current = new loaded.Marker({
        position: at.point, map: map.current, title: 'You are here', zIndex: 4,
      });
    }
    setPosition({ miles: walked, heading: at.heading });

    // Only move Street View when the view would actually differ - each move is
    // a billed panorama load.
    const previous = lastPano.current;
    const moved = previous === null
      || loaded.geometry.spherical.computeDistanceBetween(previous, at.point)
        >= STREET_VIEW_MOVE_METRES;
    if (!moved || !streetView.current || !panorama.current) return;
    lastPano.current = at.point;

    streetView.current.getPanorama(
      { location: at.point, radius: STREET_VIEW_RADIUS_METRES },
      (data, status) => {
        const pano = data?.location?.pano;
        if (status === loaded.StreetViewStatus.OK && pano && panorama.current) {
          panorama.current.setPano(pano);
          // Face along the route rather than at whatever the car was pointing.
          panorama.current.setPov({ heading: at.heading, pitch: 0 });
          setCoverage('yes');
        } else {
          setCoverage('no');
        }
      },
    );
  }, [walked]);

  useEffect(() => { place(); }, [place, ready, route]);

  /// Name the point that has been placed, then hand it to the editor.
  ///
  /// The geocode happens here rather than on every drag: it is billed per
  /// request, and only the point actually chosen needs a name.
  const openEditor = useCallback(async () => {
    const loaded = maps.current;
    const at = pin.current?.getPosition();
    if (!loaded || !draft || !at) return;
    setNaming(true);
    const described = await describePoint(loaded, at);
    setNaming(false);
    // Spread rather than an undefined label: the project builds with
    // exactOptionalPropertyTypes, where an absent property and one set to
    // undefined are not the same type.
    setSeed({ miles: draft.miles, ...(described === '' ? {} : { label: described }) });
  }, [draft]);

  if (!config.mapsKey) {
    return (
      <section className="card card--wide">
        <h2>Map</h2>
        <p className="muted">No Google Maps key is configured.</p>
      </section>
    );
  }

  if (!store.walk) {
    return (
      <section className="card card--wide">
        <h2>Map</h2>
        <p className="muted">No active walk.</p>
      </section>
    );
  }

  if (!routeId) {
    return (
      <section className="card card--wide">
        <h2>Map</h2>
        <p className="muted">
          This walk has no route yet. Build or import one on the Routes tab, then pick it under
          Walk settings — the map needs a line to place you on.
        </p>
      </section>
    );
  }

  const ahead = draft ? draft.miles - walked : 0;

  return (
    <section className="mapview">
      <div className="card card--wide">
        <div className="card__head">
          <h2>{route?.name ?? 'Route'}</h2>
          {position && (
            <p className="muted">
              {fmt.miles(position.miles)} along
              {route ? ` of ${fmt.miles(route.distanceMiles)}` : ''}
              {store.summary ? ` · ${fmt.percent(store.summary.pctComplete)}` : ''}
            </p>
          )}
        </div>

        {error && <p className="error">{error}</p>}

        <div ref={mapHolder} className="mapview__map" />

        <div className="mapview__actions">
          <button
            onClick={() => {
              const at = marker.current?.getPosition();
              if (at && map.current) { map.current.panTo(at); map.current.setZoom(12); }
            }}
            disabled={!position}
          >
            Centre on me
          </button>
          <button
            onClick={() => {
              const loaded = maps.current;
              const measured = path.current;
              if (!loaded || !map.current || !measured) return;
              const bounds = new loaded.LatLngBounds();
              for (const point of measured.points) bounds.extend(point);
              map.current.fitBounds(bounds);
            }}
            disabled={!route}
          >
            Whole route
          </button>
          {!placing && (
            <button
              onClick={() => {
                setPlacing(true);
                // A crosshair says the next click means something, and where.
                map.current?.setOptions({ draggableCursor: 'crosshair' });
              }}
              disabled={!route || !ready}
            >
              {tracking ? 'Move the checkpoint…' : 'Set a checkpoint…'}
            </button>
          )}
        </div>

        {placing && (
          <div className="mapview__placing">
            {draft ? (
              <>
                <p>
                  <strong>{fmt.miles(draft.miles)} along</strong>
                  {ahead > 0 ? ` · ${fmt.miles(ahead)} ahead of you` : ' · where you are now'}
                </p>
                {ahead <= 0 && (
                  <p className="muted">
                    A checkpoint has to be somewhere you have not reached. The marker will not go
                    behind you — drag it forward along the route.
                  </p>
                )}
                <div className="mapview__actions">
                  <button onClick={clearDraft} disabled={naming}>Cancel</button>
                  <button
                    className="primary"
                    disabled={ahead <= 0 || naming}
                    onClick={() => void openEditor()}
                  >
                    {naming ? 'Looking up where…' : 'Name this point…'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="muted">
                  Click the route to drop a marker, then drag it along the line.
                </p>
                <div className="mapview__actions">
                  <button onClick={clearDraft}>Cancel</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Worth surfacing: a route saved with one distance and drawn with
            another would put the marker in the wrong place, and the difference
            is invisible unless it is stated. */}
        {route && drawnMiles !== null && Math.abs(drawnMiles - route.distanceMiles) > 1 && (
          <p className="muted">
            The saved length is {fmt.miles(route.distanceMiles)} but the line measures{' '}
            {fmt.miles(drawnMiles)}. The marker follows the line.
          </p>
        )}
      </div>

      <div className="card card--wide">
        <div className="card__head"><h2>Street View</h2></div>
        <div ref={panoHolder} className="mapview__pano" />
        {coverage === 'no' && (
          <p className="muted">
            No Street View here — the nearest photographed road is more than{' '}
            {STREET_VIEW_RADIUS_METRES} m away. It will pick up again further along.
          </p>
        )}
      </div>

      {seed && (
        <CheckpointDialog
          store={store}
          seed={seed}
          onClose={() => { setSeed(null); clearDraft(); }}
        />
      )}
    </section>
  );
}
